import api from '@/apis'
import Vuetify from '@/plugins/vuetify'
import { useScripts } from '@/scripts/scripts.ts'

const scripts = useScripts()

// Monotonic token for latest-request-wins on the funnel: overlapping fetches (device / date-range
// switches) only let the most recent request commit, so a slower older response can't clobber newer data.
let funnelReqSeq = 0

export default {
  state: {
    loading: true,
    datepicker: {
      dates: [new Date(Date.now() - 604800000), new Date(Date.now())]
    },
    conversation_line_chart: {},
    fallback_conversation_line_chart: {},
    q_a_sessions_line_chart: {},
    llm_tokens: 0,
    count_by_day_chart: { chartdata: {} },
    average_engagement_time: 0,
    faq: [],
    funnel: {},
    funnel_error: false
  },
  mutations: {
    SET_DASHBOARD_LOADING(state, payload) {
      state.loading = payload
    },
    SET_DASHBOARD_DATEPICKER_DATES(state, payload) {
      state.datepicker.dates = payload
    },
    SET_DASHBOARD_LINE_CHART(state, payload) {
      state.conversation_line_chart = payload.conversations
      state.fallback_conversation_line_chart = payload.fallback_conversations
      state.q_a_sessions_line_chart = payload.q_a_sessions
      state.llm_tokens = payload.llm_tokens
    },
    SET_DASHBOARD_COUNT_BY_DAY_CHART(state, payload) {
      state.count_by_day_chart.chartdata = payload
    },
    SET_DASHBOARD_AVERAGE_ENGAGEMENT_TIME(state, payload) {
      state.average_engagement_time = payload
    },
    SET_DASHBOARD_FAQ(state, payload) {
      state.faq = payload
    },
    SET_DASHBOARD_FUNNEL(state, payload) {
      state.funnel = payload
    },
    SET_DASHBOARD_FUNNEL_ERROR(state, payload) {
      state.funnel_error = payload
    },
    RESET_DASHBOARD(state) {
      state.conversation_line_chart = {}
      state.fallback_conversation_line_chart = {}
      state.q_a_sessions_line_chart = {}
      state.llm_tokens = 0
      state.count_by_day_chart.chartdata = {}
      state.average_engagement_time = 0
      state.faq = []
      state.funnel = {}
      state.funnel_error = false
    }
  },
  actions: {
    async fetchDashboardSummary({ state, commit }) {
      try {
        commit('SET_DASHBOARD_LOADING', true)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.loading', ''), loading: true })

        const start_datetime = scripts.toBackendDate(state.datepicker.dates[0])
        const end_datetime = scripts.toBackendDate(state.datepicker.dates[state.datepicker.dates.length - 1])

        const [summary_graph, conversation_count, avg_engage_time, fqa] = await Promise.all([
          api.dashboard.getSummaryGraph({ start_datetime, end_datetime }),
          api.dashboard.getConversationCount({ start_datetime, end_datetime }),
          api.dashboard.getAverageEngagementTime({ start_datetime, end_datetime }),
          api.dashboard.getFAQ({ start_datetime, end_datetime })
        ])

        if (summary_graph.data.success && conversation_count.data.success && avg_engage_time.data.success && fqa.data.success) {
          commit('SET_DASHBOARD_LOADING', false)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.success', ''), icon: 'check', timeout: '2000' })
          commit('SET_DASHBOARD_LINE_CHART', summary_graph.data.data)
          commit('SET_DASHBOARD_COUNT_BY_DAY_CHART', conversation_count.data.data)
          commit('SET_DASHBOARD_AVERAGE_ENGAGEMENT_TIME', avg_engage_time.data.data)
          commit('SET_DASHBOARD_FAQ', fqa.data.data)
        } else if (!summary_graph.data.success) throw Error(summary_graph.data.message)
        else if (!conversation_count.data.success) throw Error(conversation_count.data.message)
        else if (!avg_engage_time.data.success) throw Error(avg_engage_time.data.message)
        else if (!fqa.data.success) throw Error(fqa.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    // [L-2] Independent action: its own try/catch, never touches `loading` or the main
    // dashboard state, so a funnel failure cannot take down the rest of the dashboard.
    async fetchBotisfyFunnel({ state, commit }) {
      // Latest-request-wins token: this request only owns the state if it's still the newest after its await.
      const seq = ++funnelReqSeq
      try {
        // Clear stale data first so a slower funnel fetch (e.g. on device switch) can't flash the previous device's metrics.
        commit('SET_DASHBOARD_FUNNEL', {})
        commit('SET_DASHBOARD_FUNNEL_ERROR', false)

        const start_datetime = scripts.toBackendDate(state.datepicker.dates[0])
        const end_datetime = scripts.toBackendDate(state.datepicker.dates[state.datepicker.dates.length - 1])

        const { data } = await api.dashboard.getBotisfyFunnel({ start_datetime, end_datetime })

        // A newer request has since started; drop this response so it can't overwrite fresher metrics.
        if (seq !== funnelReqSeq) return

        if (data.success) commit('SET_DASHBOARD_FUNNEL', data.data)
        else throw Error(data.message)
      } catch (error) {
        // Same guard for failures: an older error must not clear/flag the newer request's data.
        if (seq !== funnelReqSeq) return
        commit('SET_DASHBOARD_FUNNEL', {})
        commit('SET_DASHBOARD_FUNNEL_ERROR', true)
      }
    }
  }
}
